import {
  useState,
  useEffect,
  Suspense,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import {
  Layout,
  Avatar,
  Dropdown,
  Typography,
  Spin,
  Drawer,
  Modal,
  Tabs,
  Form,
  Input,
  message,
  Image,
  Select,
} from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  EditOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { io } from "socket.io-client";
import {
  addNotification,
  setNotificationCounts,
} from "app/redux/action/notifications";
import { receiveRealtimeFeedback } from "app/redux/action/feedbacks";
import axiosInstance from "app/services/api";
import {
  LayoutDashboard,
  Newspaper,
  MessageCircleWarning,
  Bot,
  GraduationCap,
  Users,
  MapPin,
  Building2,
  ShieldCheck,
  Settings,
  CalendarCheck,
  Smartphone,
  SquarePlus,
  BarChart2,
  BarChart3,
  ClipboardList,
  Megaphone,
  Home,
  ActivityIcon,
  History,
  BotMessageSquare,
  KeyRound,
} from "lucide-react";

import { useAuth } from "../../contexts/AuthContext";
import { useSelector, useDispatch } from "react-redux";
import { fetchWard } from "app/redux/action/ward";
import {
  setAuthUser,
  changeUserPassword,
  updateUserInfoAction,
} from "app/redux/action/user";
import { usePermission, PERMISSION_CODES } from "../../hooks/usePermission";

import { useResponsive } from "../../hooks/useResponsive";
import AvatarUpload from "../../components/AvatarUpload/AvatarUpload";
import AppErrorBoundary from "../../components/AppErrorBoundary/AppErrorBoundary";
import NotificationMiniPanel from "../NotificationMiniPanel/NotificationMiniPanel";
import Logo from "../../../assets/logo-cand.png";
import NotificationSound from "../../../assets/noti.mp3";
import { getSidebarCounts } from "app/services/dashboard.service";

import {
  SidebarMenu,
  SIDEBAR_ICON_SIZE,
  SIDEBAR_ICON_STROKE,
} from "../sidebar/SidebarMenu";
import { getFullUrl } from "constants/func";
import { RANK_OPTIONS } from "constants/CONSTANTS";

const { Sider, Content } = Layout;
const { Text } = Typography;

const ICON_PROPS = {
  size: SIDEBAR_ICON_SIZE,
  strokeWidth: SIDEBAR_ICON_STROKE,
};

/* ── Layout constants ─────────────────────────────────── */
const SIDEBAR_WIDTH = 270;
const COLLAPSED_WIDTH = 68;
const SIDEBAR_HEADER_H = 100;
const SIDEBAR_BG = "#FFFFFF";
const SIDEBAR_BORDER = "1px solid #E5E7EB";

/* ── Menu groups ──────────────────────────────────────── */
const MENU_GROUPS = [
  {
    key: "overview",
    label: "Điều hành",
    items: [
      {
        key: "/",
        icon: <LayoutDashboard {...ICON_PROPS} />,
        label: "Tổng quan",
        permission: PERMISSION_CODES.DASHBOARD,
        color: "#6366f1", // tím
      },
      {
        key: "/feedback-domain",
        icon: <BarChart2 {...ICON_PROPS} />,
        label: "PA lĩnh vực & địa bàn",
        permission: PERMISSION_CODES.FEEDBACK_BY_DOMAIN,
        color: "#f59e0b",
      },
      {
        key: "/officer-feedbacks",
        icon: <ActivityIcon {...ICON_PROPS} />,
        label: "Báo cáo cán bộ địa bàn",
        permission: PERMISSION_CODES.FEEDBACK_BY_OFFICER,
        color: "#10b981",
      },
      {
        key: "/miniapp-users-dashboard",
        icon: <Smartphone {...ICON_PROPS} />,
        label: "Người dùng MiniApp",
        permission: PERMISSION_CODES.MINIAPP_USERS,
        color: "#ec4899",
      },
      {
        key: "/news-analytics",
        icon: <BarChart3 {...ICON_PROPS} />,
        label: "Thống kê bài viết",
        permission: PERMISSION_CODES.NEWS_ANALYTICS,
        color: "#3b82f6",
      },
      {
        key: "/ai-usage-stats",
        icon: <BotMessageSquare {...ICON_PROPS} />,
        label: "Thống kê sử dụng AI",
        permission: PERMISSION_CODES.AI_USAGE_STATS,
        color: "#30a34a",
      },
    ],
  },
  {
    key: "citizen",
    label: "Tương tác người dân",
    items: [
      {
        key: "/news",
        icon: <Newspaper {...ICON_PROPS} />,
        label: "Tin tức",
        color: "#f59e0b",
        permission: PERMISSION_CODES.NEWS,
      },
      {
        key: "/feedback",
        icon: <MessageCircleWarning {...ICON_PROPS} />,
        label: "Phản ánh",
        color: "#ef4444",
        permission: PERMISSION_CODES.FEEDBACKS,
      },
      {
        key: "/appointments",
        icon: <CalendarCheck {...ICON_PROPS} />,
        label: "Lịch tiếp dân",
        color: "#06b6d4",
        permission: PERMISSION_CODES.APPOINTMENTS,
      },
      {
        key: "/services",
        icon: <Building2 {...ICON_PROPS} />,
        label: "Dịch vụ công",
        color: "#8b5cf6",
        permission: PERMISSION_CODES.PUBLIC_SERVICE_LINKS,
      },
      {
        key: "/ai-conversations",
        icon: <Bot {...ICON_PROPS} />,
        label: "Trợ lý AI CAND",
        color: "#0ea5e9",
        permission: PERMISSION_CODES.CHATS,
      },
      {
        key: "/commitment",
        icon: <Building2 {...ICON_PROPS} />,
        label: "Cam kết",
        color: "#10b981",
        permission: PERMISSION_CODES.COMMITMENTS,
      },
      {
        key: "/task-managements",
        icon: <ClipboardList {...ICON_PROPS} />,
        label: "Quản lý nhiệm vụ",
        color: "#1070b9ff",
        permission: PERMISSION_CODES.TASK_MANAGERMENTS,
      },
    ],
  },
  {
    key: "area",
    label: "Địa bàn & tổ chức",
    items: [
      {
        key: "/officers",
        icon: <Users {...ICON_PROPS} />,
        label: "Cán bộ địa bàn",
        color: "#f97316",
        permission: PERMISSION_CODES.AREA_OFFICERS,
      },
      {
        key: "/areas",
        icon: <MapPin {...ICON_PROPS} />,
        label: "Bản đồ khu vực",
        color: "#84cc16",
        permission: PERMISSION_CODES.AREAS,
      },
    ],
  },
  {
    key: "miniapp-accounts",
    label: "Dữ liệu cư dân",
    items: [
      {
        key: "/miniapp-accounts",
        icon: <Smartphone {...ICON_PROPS} />,
        label: "Tài khoản Miniapp",
        color: "#ec4899",
        permission: PERMISSION_CODES.MINIAPP_ACCOUNTS,
      },
      {
        key: "/establishing-house-numbers",
        icon: <Home {...ICON_PROPS} />,
        label: "Thiết lập số nhà TDP",
        color: "#2563eb",
        permission: PERMISSION_CODES.ESTABLISHING_HOUSE_NUMBERS,
      },
    ],
  },
  {
    key: "truyen-thong",
    label: "Chỉ đạo và thông báo",
    items: [
      {
        key: "/broadcasts",
        icon: <Megaphone {...ICON_PROPS} />,
        label: "Thông báo Broadcast",
        color: "#ec4899",
        permission: PERMISSION_CODES.BROADCASTS,
      },
    ],
  },
  {
    key: "system",
    label: "Hệ thống",
    items: [
      {
        key: "/users",
        icon: <ShieldCheck {...ICON_PROPS} />,
        label: "Quản lý tài khoản",
        color: "#64748b",
        permission: PERMISSION_CODES.USERS,
      },
      {
        key: "/roles",
        icon: <KeyRound {...ICON_PROPS} />,
        label: "Phân quyền hệ thống",
        color: "#7c3aed",
        adminOnly: true,
      },
      {
        key: "/audit-logs",
        icon: <History {...ICON_PROPS} />,
        label: "Lịch sử hoạt động",
        color: "#64748b",
        permission: PERMISSION_CODES.AUDIT_LOGS,
      },
      {
        key: "/ai-training",
        icon: <GraduationCap {...ICON_PROPS} />,
        label: "Training AI",
        color: "#3f9d2f",
        permission: PERMISSION_CODES.AI_TRAINING,
      },
      {
        key: "/settings",
        icon: <Settings {...ICON_PROPS} />,
        label: "Cài đặt",
        color: "#a78bfa",
        permission: PERMISSION_CODES.WARDS,
      },
    ],
  },
];

const roleLabels = {
  chief: "Trưởng Công an",
  deputy_chief: "Phó Công an",
  team_leader: "Tổ trưởng",
  officer: "Cán bộ",
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);

  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const isStandaloneApp = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  navigator.standalone === true;

const getPushDeviceInfo = (provider) => ({
  provider,
  userAgent: navigator.userAgent,
  platform: navigator.platform,
  language: navigator.language,
  standalone: isStandaloneApp(),
});

const NOTIFICATION_SOUND_URL = NotificationSound;
const NOTIFICATION_SOUND_GAIN = (() => {
  const gain = Number(process.env.REACT_APP_NOTIFICATION_SOUND_GAIN || 2);
  if (!Number.isFinite(gain) || gain <= 0) return 2;
  return Math.min(gain, 4);
})();
const NOTIFICATION_TOAST_DEDUPE_MS = 5000;

const normalizeIncomingNotification = (notification = {}) => {
  const nestedData =
    notification.data && typeof notification.data === "object"
      ? notification.data
      : {};

  return {
    ...nestedData,
    ...notification,
    id:
      notification.id ||
      notification._id ||
      notification.notificationId ||
      nestedData.id ||
      nestedData._id ||
      nestedData.notificationId,
    title: notification.title || nestedData.title || "Thông báo Công an Phường",
    content:
      notification.content ||
      notification.body ||
      nestedData.content ||
      nestedData.body ||
      "",
    link:
      notification.link ||
      notification.click_action ||
      nestedData.link ||
      nestedData.click_action ||
      "",
    priority: notification.priority || nestedData.priority || "medium",
    category: notification.category || nestedData.category || "other",
    createdAt:
      notification.createdAt ||
      nestedData.createdAt ||
      new Date().toISOString(),
  };
};

const getNotificationToastType = (notification) => {
  if (notification.priority === "critical") return "error";
  if (notification.priority === "warning") return "warning";
  return "info";
};

const getNotificationDedupeKey = (notification) =>
  notification.id ||
  `${notification.title || ""}|${notification.content || ""}|${
    notification.createdAt || ""
  }`;

const AdminLayout = () => {
  const [collapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const currentPathname = location.pathname;
  const isBroadcastDetailUrl = /^\/broadcasts\/([^/]+)\/?$/i.test(
    currentPathname,
  );
  const activeMenuKey = isBroadcastDetailUrl ? "/" : currentPathname;
  const navigate = useNavigate();
  const { logoutAction, isLoading } = useAuth();
  const user = useSelector((state) => state.auth.user);
  const { isMobile } = useResponsive();
  const dispatch = useDispatch();
  const { currentWard } = useSelector((state) => state.ward);
  const isAnyReduxLoading = useSelector((state) =>
    Object.values(state).some(
      (slice) => slice && typeof slice === "object" && slice.loading,
    ),
  );
  const queryFetchingCount = useIsFetching();
  const queryMutatingCount = useIsMutating();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [sidebarCounts, setSidebarCounts] = useState({});
  const sidebarCountsTimerRef = useRef(null);
  const sidebarCountsRequestingRef = useRef(false);
  const [infoForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState("info");
  const [notifBlocked, setNotifBlocked] = useState(false);
  const [notifBannerDismissed, setNotifBannerDismissed] = useState(false);
  const [notificationPermission, setNotificationPermission] =
    useState("default");
  const [notifRegistering, setNotifRegistering] = useState(false);
  const recentNotificationToastRef = useRef(new Map());
  const notificationAudioRef = useRef(null);
  const notificationAudioContextRef = useRef(null);
  const notificationAudioGainRef = useRef(null);

  useEffect(() => {
    if (!currentWard) {
      dispatch(fetchWard());
    }
  }, [dispatch, currentWard]);

  // Detect notification permission on mount. Browsers can block prompts that are
  // not triggered by a user gesture, so the actual permission request is button-driven.
  useEffect(() => {
    if (!("Notification" in window)) {
      setNotificationPermission("unsupported");
      return;
    }

    setNotificationPermission(Notification.permission);
    setNotifBlocked(Notification.permission === "denied");
  }, []);

  useEffect(() => {
    if (
      isStandaloneApp() ||
      !NOTIFICATION_SOUND_URL ||
      typeof Audio === "undefined"
    ) {
      return undefined;
    }

    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.preload = "auto";
    audio.volume = 1;
    notificationAudioRef.current = audio;

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (AudioContextCtor) {
      try {
        const audioContext = new AudioContextCtor();
        const source = audioContext.createMediaElementSource(audio);
        const gainNode = audioContext.createGain();
        gainNode.gain.value = NOTIFICATION_SOUND_GAIN;
        source.connect(gainNode);
        gainNode.connect(audioContext.destination);
        notificationAudioContextRef.current = audioContext;
        notificationAudioGainRef.current = gainNode;
      } catch {
        notificationAudioContextRef.current = null;
        notificationAudioGainRef.current = null;
      }
    }

    return () => {
      audio.pause();
      notificationAudioContextRef.current?.close?.().catch(() => {});
      notificationAudioRef.current = null;
      notificationAudioContextRef.current = null;
      notificationAudioGainRef.current = null;
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    if (isStandaloneApp()) return;

    const audio = notificationAudioRef.current;
    if (!audio) return;

    const play = async () => {
      try {
        const audioContext = notificationAudioContextRef.current;
        if (audioContext?.state === "suspended") {
          await audioContext.resume();
        }

        if (notificationAudioGainRef.current) {
          notificationAudioGainRef.current.gain.value = NOTIFICATION_SOUND_GAIN;
        }

        audio.currentTime = 0;
        await audio.play();
      } catch {
        // Browser autoplay policies can block programmatic sound, especially on mobile.
      }
    };

    play();
  }, []);

  const showInAppNotification = useCallback(
    (incomingNotification) => {
      const normalizedNotification =
        normalizeIncomingNotification(incomingNotification);
      const dedupeKey = getNotificationDedupeKey(normalizedNotification);
      const now = Date.now();
      const recentToasts = recentNotificationToastRef.current;
      const lastShownAt = recentToasts.get(dedupeKey);

      recentToasts.forEach((shownAt, key) => {
        if (now - shownAt > 30000) recentToasts.delete(key);
      });

      if (lastShownAt && now - lastShownAt < NOTIFICATION_TOAST_DEDUPE_MS) {
        return normalizedNotification;
      }

      recentToasts.set(dedupeKey, now);
      playNotificationSound();

      const type = getNotificationToastType(normalizedNotification);
      const msgKey = `notif-${dedupeKey || now}`;
      let touchStartY = null;

      message[type]({
        key: msgKey,
        content: (
          <span
            style={{ fontSize: 13.5, display: "block" }}
            onTouchStart={(e) => {
              touchStartY = e.touches[0].clientY;
            }}
            onTouchEnd={(e) => {
              if (touchStartY === null) return;
              const deltaY = e.changedTouches[0].clientY - touchStartY;
              if (deltaY < -40) {
                message.destroy(msgKey);
              }
              touchStartY = null;
            }}
          >
            <strong>{normalizedNotification.title}</strong>
            {normalizedNotification.type !== "BROADCAST_NEW" &&
              normalizedNotification.content &&
              `: ${normalizedNotification.content}`}
          </span>
        ),
        duration: 5,
      });

      return normalizedNotification;
    },
    [playNotificationSound],
  );

  const getSocketUrl = () => {
    const url =
      process.env.REACT_APP_SOCKET_URL ||
      process.env.REACT_APP_MEDIA_API_BASE_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      "";
    return url.replace(/\/api\/?$/, "");
  };

  // 1. Initialize real-time WebSocket connection
  useEffect(() => {
    if (!user?.id) return undefined;

    const socketUrl = getSocketUrl();
    if (!socketUrl) return undefined;

    const socket = io(socketUrl, {
      autoConnect: true,
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    socket.on("connect", () => {
      socket.emit("register", { userId: user.id, userModel: "Users" });
    });

    socket.on("new_notification", (newNotif) => {
      const normalizedNotification = normalizeIncomingNotification(newNotif);
      dispatch(addNotification(normalizedNotification, user.id));
      showInAppNotification(normalizedNotification);
    });

    socket.on("notification_counts", (counts) => {
      dispatch(setNotificationCounts(counts));
    });

    socket.on("feedback_realtime", (payload) => {
      dispatch(receiveRealtimeFeedback(payload));
    });

    socket.on("disconnect", (reason) => {});

    return () => {
      socket.off("connect");
      socket.off("new_notification");
      socket.off("notification_counts");
      socket.off("feedback_realtime");
      socket.off("disconnect");
      socket.disconnect();
    };
  }, [dispatch, showInAppNotification, user?.id]);

  useEffect(() => {
    if (!user?.id || !("serviceWorker" in navigator)) return undefined;

    const handleServiceWorkerMessage = (event) => {
      const eventData = event.data || {};
      const isNotificationEvent =
        eventData.type === "WEB_PUSH_NOTIFICATION_RECEIVED" ||
        eventData.type === "WEB_PUSH_NOTIFICATION_CLICKED";

      if (!isNotificationEvent) return;

      if (
        eventData.type === "WEB_PUSH_NOTIFICATION_RECEIVED" &&
        document.visibilityState === "visible"
      ) {
        showInAppNotification(eventData.payload || {});
      }
    };

    navigator.serviceWorker.addEventListener(
      "message",
      handleServiceWorkerMessage,
    );

    return () => {
      navigator.serviceWorker.removeEventListener(
        "message",
        handleServiceWorkerMessage,
      );
    };
  }, [showInAppNotification, user?.id]);

  const registerNativeWebPush = useCallback(
    async ({ requestPermission = false } = {}) => {
      if (!user?.id) return false;

      if (
        !("Notification" in window) ||
        !("serviceWorker" in navigator) ||
        !("PushManager" in window)
      ) {
        setNotificationPermission("unsupported");
        return false;
      }

      setNotifRegistering(true);

      try {
        const publicKey = process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY;

        if (!publicKey) {
          return false;
        }

        let permission = Notification.permission;
        if (permission === "default" && requestPermission) {
          permission = await Notification.requestPermission();
        }

        setNotificationPermission(permission);
        setNotifBlocked(permission === "denied");

        if (permission !== "granted") {
          return false;
        }

        const registration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
          { scope: "/" },
        );
        const readyRegistration = await navigator.serviceWorker.ready;
        const activeRegistration = readyRegistration || registration;
        let subscription =
          await activeRegistration.pushManager.getSubscription();

        if (!subscription) {
          subscription = await activeRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          });
        }

        await axiosInstance.post(
          "notifications/register-web-push-subscription",
          {
            subscription: subscription.toJSON(),
            deviceInfo: getPushDeviceInfo("webpush"),
          },
        );

        setNotifBannerDismissed(true);
        return true;
      } catch (pushErr) {
        return false;
      } finally {
        setNotifRegistering(false);
      }
    },
    [user?.id],
  );

  // 3. Register PWA Native Web Push when permission already exists.
  useEffect(() => {
    if (!user?.id || !("Notification" in window)) return;

    setNotificationPermission(Notification.permission);
    setNotifBlocked(Notification.permission === "denied");

    if (Notification.permission === "granted") {
      registerNativeWebPush();
    }
  }, [registerNativeWebPush, user?.id]);

  const { filterMenuGroups } = usePermission();
  const filteredMenuGroups = useMemo(
    () => filterMenuGroups(MENU_GROUPS),
    [filterMenuGroups],
  );
  const menuGroupsWithBadges = useMemo(() => {
    const badgeByKey = {
      "/feedback": sidebarCounts.feedbacks,
      "/appointments": sidebarCounts.receptionSchedules,
      "/commitment": sidebarCounts.commitments,
      "/miniapp-accounts": sidebarCounts.miniappAccounts,
      "/news": sidebarCounts.newsArticles,
    };

    const tooltipTemplates = {
      "/feedback": (val) => `Có ${val} phản ánh cần xử lý`,
      "/appointments": (val) => `Hôm nay có ${val} lịch tiếp dân`,
      "/commitment": (val) => `Hôm nay có ${val} cam kết được ký`,
      "/miniapp-accounts": (val) => `Hôm nay có ${val} tài khoản mới`,
      "/news": (val) => `Hôm nay có ${val} tin tức mới`,
    };

    return filteredMenuGroups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        const badge = badgeByKey[item.key];
        if (typeof badge === "number" && badge > 0) {
          const template = tooltipTemplates[item.key];
          const tooltip = template
            ? template(badge)
            : `Có ${badge} ${item.label.toLowerCase()} cần xử lý`;
          return { ...item, badge, tooltip };
        }
        return item;
      }),
    }));
  }, [filteredMenuGroups, sidebarCounts]);

  useEffect(() => {
    setSidebarCounts({});
  }, [user?.id]);

  useEffect(() => {
    const isAppIdle =
      !isLoading &&
      !isAnyReduxLoading &&
      queryFetchingCount === 0 &&
      queryMutatingCount === 0;

    if (sidebarCountsTimerRef.current) {
      clearTimeout(sidebarCountsTimerRef.current);
      sidebarCountsTimerRef.current = null;
    }

    if (!user?.id || !isAppIdle) return undefined;

    let cancelled = false;

    sidebarCountsTimerRef.current = setTimeout(() => {
      if (sidebarCountsRequestingRef.current) return;

      sidebarCountsRequestingRef.current = true;
      getSidebarCounts()
        .then((counts) => {
          if (cancelled) return;
          setSidebarCounts({
            feedbacks: Number(counts?.feedbacks || 0),
            receptionSchedules: Number(counts?.receptionSchedules || 0),
            commitments: Number(counts?.commitments || 0),
            miniappAccounts: Number(counts?.miniappAccounts || 0),
            newsArticles: Number(counts?.newsArticles || 0),
          });
        })
        .catch(() => {})
        .finally(() => {
          sidebarCountsRequestingRef.current = false;
        });
    }, 800);

    return () => {
      cancelled = true;
      if (sidebarCountsTimerRef.current) {
        clearTimeout(sidebarCountsTimerRef.current);
        sidebarCountsTimerRef.current = null;
      }
    };
  }, [
    currentPathname,
    isAnyReduxLoading,
    isLoading,
    queryFetchingCount,
    queryMutatingCount,
    user?.id,
  ]);

  const handleUpdateProfile = async (values) => {
    setProfileLoading(true);
    try {
      if (activeTab === "info") {
        const res = await dispatch(
          updateUserInfoAction(
            user.id,
            values.fullName,
            values.phone,
            values.avatarUrl,
            values.rank,
          ),
        );
        dispatch(setAuthUser({ ...user, ...res, roleId: user.roleId }));
        message.success("Cập nhật thông tin thành công!");
      } else {
        await dispatch(
          changeUserPassword(
            user.id,
            values.oldPassword,
            values.password,
            values.confirmPassword,
          ),
        );
        message.success("Đổi mật khẩu thành công, vui lòng đăng nhập lại!");
        setProfileModalOpen(false);
        setTimeout(() => {
          logoutAction();
        }, 1500);
        return;
      }
      setProfileModalOpen(false);
    } catch (error) {
      message.error(error.message || "Thao tác thất bại!");
    } finally {
      setProfileLoading(false);
    }
  };

  const openProfileModal = () => {
    setActiveTab("info");
    infoForm.resetFields();
    infoForm.setFieldsValue({
      fullName: user?.fullName,
      phone: user?.phone,
      avatarUrl: user?.avatarUrl,
      rank: user?.rank,
    });
    passwordForm.resetFields();
    setProfileModalOpen(true);
  };

  const renderProfileModal = () => (
    <Modal
      title={
        <div style={{ paddingBottom: 8, borderBottom: "1px solid #f0f0f0" }}>
          <Text style={{ fontSize: 18, fontWeight: 600 }}>
            Chỉnh sửa thông tin cá nhân
          </Text>
        </div>
      }
      open={profileModalOpen}
      onCancel={() => setProfileModalOpen(false)}
      onOk={() =>
        activeTab === "info" ? infoForm.submit() : passwordForm.submit()
      }
      okText="Cập nhật"
      cancelText="Hủy"
      confirmLoading={profileLoading}
      width={isMobile ? "95%" : 500}
      style={{ top: 20 }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "info",
            label: (
              <span style={{ fontSize: 15, fontWeight: 500 }}>
                <UserOutlined /> Thông tin chính
              </span>
            ),
            children: (
              <Form
                form={infoForm}
                layout="vertical"
                onFinish={handleUpdateProfile}
                requiredMark={true}
              >
                <div style={{ padding: "12px 0" }}>
                  <div style={{ textAlign: "center", marginBottom: 20 }}>
                    <Form.Item
                      name="avatarUrl"
                      label={
                        <span style={{ fontSize: 15, fontWeight: 500 }}>
                          Ảnh đại diện
                        </span>
                      }
                    >
                      <AvatarUpload />
                    </Form.Item>
                  </div>
                  <Form.Item
                    name="fullName"
                    label={
                      <span style={{ fontSize: 15, fontWeight: 500 }}>
                        Họ và tên
                      </span>
                    }
                    rules={[
                      { required: true, message: "Vui lòng nhập họ tên" },
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                      placeholder="Nguyễn Văn A"
                      style={{ borderRadius: 6 }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="phone"
                    label={
                      <span style={{ fontSize: 15, fontWeight: 500 }}>
                        Số điện thoại
                      </span>
                    }
                    rules={[
                      {
                        required: true,
                        message: "Vui lòng nhập số điện thoại",
                      },
                      {
                        pattern: /^0\d{9,10}$/,
                        message: "Số điện thoại phải từ 10 đến 11 chữ số",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Nhập số điện thoại"
                      maxLength={11}
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, "");
                      }}
                      style={{ borderRadius: 6 }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="rank"
                    label={
                      <span style={{ fontSize: 15, fontWeight: 500 }}>
                        Quân hàm
                      </span>
                    }
                    rules={[
                      { required: true, message: "Vui lòng chọn quân hàm" },
                    ]}
                  >
                    <Select
                      placeholder="Chọn quân hàm"
                      showSearch
                      optionFilterProp="label"
                      style={{ borderRadius: 6 }}
                    >
                      {RANK_OPTIONS.map((r) => (
                        <Select.Option
                          key={r.value}
                          value={r.value}
                          label={r.label}
                        >
                          {r.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </Form>
            ),
          },
          {
            key: "password",
            label: (
              <span style={{ fontSize: 15, fontWeight: 500 }}>
                <LockOutlined /> Mật khẩu
              </span>
            ),
            children: (
              <Form
                form={passwordForm}
                layout="vertical"
                onFinish={handleUpdateProfile}
                requiredMark={true}
              >
                <div style={{ padding: "12px 0" }}>
                  <Form.Item
                    name="oldPassword"
                    label={
                      <span style={{ fontSize: 15, fontWeight: 500 }}>
                        Mật khẩu hiện tại
                      </span>
                    }
                    rules={[
                      { required: true, message: "Vui lòng nhập mật khẩu cũ" },
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
                      placeholder="Nhập mật khẩu hiện tại"
                      style={{ borderRadius: 6 }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    label={
                      <span style={{ fontSize: 15, fontWeight: 500 }}>
                        Mật khẩu mới
                      </span>
                    }
                    dependencies={["oldPassword"]}
                    rules={[
                      { required: true, message: "Vui lòng nhập mật khẩu mới" },
                      { min: 8, message: "Mật khẩu tối thiểu 8 ký tự" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            !value ||
                            getFieldValue("oldPassword") !== value
                          ) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(
                              "Mật khẩu mới không được trùng với mật khẩu hiện tại!",
                            ),
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
                      placeholder="Nhập mật khẩu mới"
                      style={{ borderRadius: 6 }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="confirmPassword"
                    label={
                      <span style={{ fontSize: 15, fontWeight: 500 }}>
                        Xác nhận mật khẩu
                      </span>
                    }
                    dependencies={["password"]}
                    rules={[
                      {
                        required: true,
                        message: "Vui lòng xác nhận mật khẩu mới",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Mật khẩu xác nhận không khớp!"),
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
                      placeholder="Xác nhận mật khẩu mới"
                      style={{ borderRadius: 6 }}
                    />
                  </Form.Item>
                </div>
              </Form>
            ),
          },
        ]}
      />
    </Modal>
  );

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  /* ── User dropdown ── */
  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: `${user?.fullName || "Cán bộ"}`,
      disabled: true,
    },
    {
      key: "edit-profile",
      icon: <EditOutlined />,
      label: "Chỉnh sửa thông tin",
      onClick: openProfileModal,
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Đăng xuất",
      danger: true,
      onClick: logoutAction,
    },
  ];

  const handleMenuClick = (key) => {
    navigate(key);
    if (isMobile) setDrawerOpen(false);
  };

  /* ── Quick actions ── */
  const quickActions = [
    {
      key: "add-news",
      requiredAction: { menuCode: "News", actionCode: "Create" },
      icon: <SquarePlus size={18} strokeWidth={2.5} />,
      label: "Thêm tin tức",
      color: "#16a34a",
      onClick: () => {
        navigate("/news", { state: { action: "openCreate" } });
        if (isMobile) setDrawerOpen(false);
      },
    },
    {
      key: "add-task",
      requiredAction: { menuCode: "TaskManagement", actionCode: "Create" },
      icon: <ClipboardList size={18} strokeWidth={2.5} />,
      label: "Thêm nhiệm vụ",
      color: "#2563eb",
      onClick: () => {
        navigate("/task-managements", { state: { action: "openCreate" } });
        if (isMobile) setDrawerOpen(false);
      },
    },
    {
      key: "add-broadcast",
      requiredAction: { menuCode: "Broadcast", actionCode: "Create" },
      icon: <Megaphone size={18} strokeWidth={2.5} />,
      label: "Tạo thông báo mới",
      color: "#9333ea",
      onClick: () => {
        navigate("/broadcasts", { state: { action: "openCreate" } });
        if (isMobile) setDrawerOpen(false);
      },
    },
  ];

  const showSidebarLabel = isMobile || !collapsed;
  const routeFallback = (
    <div
      style={{
        minHeight: isMobile ? "calc(100vh - 76px)" : "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Spin size="large" tip="Đang tải trang..." />
    </div>
  );

  /* ── Sidebar header ── */
  const sidebarHeader = (
    <div
      style={{
        height: SIDEBAR_HEADER_H,
        display: "flex",
        alignItems: "center",
        padding: collapsed && !isMobile ? "0 14px" : "0 16px",
        gap: 12,
        borderBottom: "1px solid #E5E7EB",
        flexShrink: 0,
        justifyContent: collapsed && !isMobile ? "center" : "flex-start",
        background: process.env.REACT_APP_BACKGROUND_COLOR || "#295e18",
        borderBottomRightRadius: 20,
      }}
    >
      <img
        src={Logo}
        alt="Logo"
        style={{
          width: 50,
          height: 50,
          objectFit: "contain",
          flexShrink: 0,
          filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.18))",
        }}
      />
      {showSidebarLabel && (
        <div style={{ minWidth: 0, lineHeight: 1 }}>
          <div
            style={{
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              lineHeight: 1.25,
            }}
          >
            {currentWard?.wardName}
          </div>
          <div
            style={{
              color: "#fff",
              fontSize: 12,
              marginTop: 4,
              lineHeight: 1.4,
            }}
          >
            Trung thành nhất - Kỷ luật nhất - Gần dân nhất
          </div>
        </div>
      )}
    </div>
  );

  /* ── Sidebar footer: user info + bell + logout ── */
  const sidebarFooter = (
    <div
      style={{
        flexShrink: 0,
        padding: collapsed && !isMobile ? "10px 0 14px" : "0 6px 14px 10px",
        borderTop: "1px solid #E5E7EB",
      }}
    >
      {/* Footer icons */}
      <div
        style={{
          display: "flex",
          flexDirection: collapsed && !isMobile ? "column" : "row",
          alignItems: "center",
          padding: collapsed && !isMobile ? "8px" : "6px 8px 6px 10px",
          background: process.env.REACT_APP_BACKGROUND_COLOR || "#295e18",
          borderRadius: 14,
          marginTop: 10,
          boxShadow: "0 4px 12px rgba(41, 94, 24, 0.25)",
          gap: collapsed && !isMobile ? 12 : 4,
          transition: "all 0.3s ease",
        }}
      >
        {/* Avatar + Name/Role — clickable dropdown */}
        {!collapsed || isMobile ? (
          <Dropdown menu={{ items: userMenuItems }} placement="topRight">
            <div
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 10,
                flex: 1,
                minWidth: 0,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <div style={{ width: 34, height: 34, flexShrink: 0 }}>
                <Image
                  width={34}
                  height={34}
                  src={getFullUrl(user?.avatarUrl)}
                  style={{
                    borderRadius: "50%",
                    objectFit: "cover",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    border: `1.5px solid rgba(255,255,255,0.4)`,
                  }}
                  preview={{ mask: null }}
                  placeholder={
                    <Avatar
                      size={34}
                      style={{
                        backgroundColor: "rgba(255,255,255,0.2)",
                        border: `1.5px solid rgba(255,255,255,0.4)`,
                        color: "#FFFFFF",
                        fontWeight: 700,
                      }}
                      icon={<UserOutlined />}
                    >
                      {user?.fullName?.[0]}
                    </Avatar>
                  }
                />
              </div>
              <div style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
                <Text
                  strong
                  style={{
                    display: "block",
                    fontSize: 13,
                    color: "#FFFFFF",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user?.fullName}
                </Text>
                <Text
                  style={{
                    fontSize: 10.5,
                    color: "rgba(255,255,255,0.75)",
                    display: "block",
                  }}
                >
                  {roleLabels[user?.role || ""] || user?.role}
                </Text>
              </div>
            </div>
          </Dropdown>
        ) : (
          <Dropdown menu={{ items: userMenuItems }} placement="topRight">
            <div style={{ width: 36, height: 36, flexShrink: 0 }}>
              <Image
                width={36}
                height={36}
                src={user?.avatarUrl}
                style={{
                  borderRadius: "50%",
                  objectFit: "cover",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  border: `1.5px solid rgba(255,255,255,0.4)`,
                }}
                preview={{ mask: null }}
                placeholder={
                  <Avatar
                    size={36}
                    style={{
                      backgroundColor: "rgba(255,255,255,0.2)",
                      border: `1.5px solid rgba(255,255,255,0.4)`,
                      color: "#FFFFFF",
                      fontWeight: 700,
                    }}
                    icon={<UserOutlined />}
                  >
                    {user?.fullName?.[0]}
                  </Avatar>
                }
              />
            </div>
          </Dropdown>
        )}

        {/* Vertical divider if not collapsed */}
        {!collapsed && (
          <div
            style={{
              width: 1,
              height: 24,
              background: "rgba(255,255,255,0.15)",
              margin: "0 4px",
            }}
          />
        )}

        {/* Bell */}
        <NotificationMiniPanel
          role={user?.role}
          onCloseSidebar={() => setDrawerOpen(false)}
        />
      </div>
    </div>
  );

  /* ── Sidebar content ── */
  const sidebarContent = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {sidebarHeader}
      <SidebarMenu
        groups={menuGroupsWithBadges}
        collapsed={!isMobile && collapsed}
        activeKey={activeMenuKey}
        onSelect={handleMenuClick}
        quickActions={quickActions}
      />
      {sidebarFooter}
    </div>
  );

  const shouldShowNotificationBanner =
    user?.id &&
    !notifBannerDismissed &&
    (notificationPermission === "default" || notifBlocked);

  const renderNotificationPermissionBanner = () => {
    if (!shouldShowNotificationBanner) return null;

    const isDenied = notifBlocked || notificationPermission === "denied";

    return (
      <div
        style={{
          position: "fixed",
          top: isMobile ? 56 : 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: isDenied
            ? "linear-gradient(90deg, #78350f 0%, #92400e 100%)"
            : "linear-gradient(90deg, #14532d 0%, #166534 100%)",
          color: "#fff",
          padding: isMobile ? "10px 16px" : "10px 20px",
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 10 : 12,
          flexWrap: isMobile ? "wrap" : "nowrap",
          fontSize: 13,
          boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
        }}
      >
        <span
          style={{ flex: 1, minWidth: isMobile ? 220 : 320, lineHeight: 1.5 }}
        >
          {isDenied ? (
            <>
              <strong>Thông báo đang bị chặn.</strong> Để nhận thông báo, hãy mở
              cài đặt trang web trong trình duyệt, chọn{" "}
              <strong>Thông báo - Cho phép</strong>, sau đó tải lại trang.
            </>
          ) : (
            <>
              <strong>Bật thông báo.</strong> Bấm bật để đăng ký thiết bị nhận
              cảnh báo khi ứng dụng chạy nền hoặc đã đóng. Trên iPhone/iPad, hãy
              mở web bằng biểu tượng đã thêm vào Màn hình chính trước khi bật.
            </>
          )}
        </span>
        {!isDenied && (
          <button
            type="button"
            disabled={notifRegistering}
            onClick={() => registerNativeWebPush({ requestPermission: true })}
            style={{
              all: "unset",
              cursor: notifRegistering ? "not-allowed" : "pointer",
              opacity: notifRegistering ? 0.7 : 1,
              padding: isMobile ? "4px 10px" : "4px 14px",
              borderRadius: 6,
              background: "rgba(255,255,255,0.22)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {notifRegistering ? "Đang bật..." : "Bật thông báo"}
          </button>
        )}
        <button
          type="button"
          onClick={() => setNotifBannerDismissed(true)}
          style={{
            all: "unset",
            cursor: "pointer",
            padding: isMobile ? "4px 10px" : "4px 14px",
            borderRadius: 6,
            background: "rgba(255,255,255,0.18)",
            color: "#fff",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {isDenied ? "Đã hiểu" : "Để sau"}
        </button>
      </div>
    );
  };

  /* ── Mobile layout ── */
  if (isMobile) {
    return (
      <Layout style={{ minHeight: "100vh", background: "#F0F4F0" }}>
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: 52,
            background: "#FFFFFF",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
            zIndex: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            style={{
              all: "unset",
              cursor: "pointer",
              fontSize: 20,
              color: "#374151",
              display: "flex",
              alignItems: "center",
              padding: 4,
            }}
          >
            <MenuOutlined />
          </button>

          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              color: "#295e18",
              letterSpacing: "0.03em",
              textTransform: "uppercase",
            }}
          >
            {currentWard?.wardName || "Công An Phường"}
          </div>

          <div style={{ display: "flex", alignItems: "center" }}>
            <NotificationMiniPanel role={user?.role} isMobileTopbar={true} />
          </div>
        </div>
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={272}
          push={false}
          styles={{
            body: { padding: 0, background: SIDEBAR_BG },
            header: { display: "none" },
          }}
        >
          {sidebarContent}
        </Drawer>
        <Layout style={{ paddingTop: 52 }}>
          <Content style={{ padding: 12, minHeight: 280 }}>
            <AppErrorBoundary resetKey={currentPathname}>
              <Suspense fallback={routeFallback}>
                <Outlet />
              </Suspense>
            </AppErrorBoundary>
          </Content>
        </Layout>
        {renderProfileModal()}
        {renderNotificationPermissionBanner()}
      </Layout>
    );
  }

  /* ── Desktop layout ── */
  return (
    <Layout style={{ minHeight: "100vh", background: "#F0F4F0" }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={SIDEBAR_WIDTH}
        collapsedWidth={COLLAPSED_WIDTH}
        style={{
          background: SIDEBAR_BG,
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10,
          boxShadow: "2px 0 12px rgba(0,0,0,0.07)",
          borderRight: SIDEBAR_BORDER,
          overflow: "hidden",
        }}
      >
        {sidebarContent}
      </Sider>

      {/* Floating collapse toggle button */}
      {/* <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? "Mở rộng menu" : "Thu gọn menu"}
        style={{
          position: "fixed",
          bottom: 20,
          left: collapsed ? COLLAPSED_WIDTH - 10 : SIDEBAR_WIDTH - 10,
          transform: "none",
          zIndex: 20,
          width: 35,
          height: 35,
          border: "none",
          background: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "#374151",
          transition: "left 0.2s ease, color 0.15s",
          padding: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "#111827";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "#374151";
        }}
      >
        {collapsed ? (
          <MenuUnfoldOutlined style={{ fontSize: 16 }} />
        ) : (
          <MenuFoldOutlined style={{ fontSize: 16 }} />
        )}
      </button> */}

      <Layout
        style={{
          marginLeft: collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH,
          transition: "margin-left 0.2s ease",
        }}
      >
        <Content style={{ minHeight: 280 }}>
          <AppErrorBoundary resetKey={currentPathname}>
            <Suspense fallback={routeFallback}>
              <Outlet />
            </Suspense>
          </AppErrorBoundary>
        </Content>
      </Layout>
      {renderProfileModal()}
      {renderNotificationPermissionBanner()}
    </Layout>
  );
};

export default AdminLayout;
